import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "./api";
import { useAuth } from "../context/AuthContext";

// Phase 174: the same browser session can switch between a person's role accounts — hooks that cache
// per-user data must refetch when the signed-in account changes (not only when the municipality does).
function useSignedInUserId() {
  const ctx = useAuth();
  return ctx?.auth?.user?.id || null;
}

// Effective module access LEVELS for the logged-in user (role defaults + personal overrides).
// Levels: "none" (hidden) | "view" | "edit" | "write". Legacy boolean false === "none".
// Defaults to "everything allowed" while loading so the UI never flashes empty.
export function useRoleModules(code) {
  const uid = useSignedInUserId();
  const [mods, setMods] = useState(null);
  const [tenantMods, setTenantMods] = useState(null);   // Phase 199: per-municipality module switches (e.g. smart_parking)
  useEffect(() => {
    let alive = true;
    setMods(null);
    setTenantMods(null);
    api.get(`/t/${code}/role-modules/my`)
      .then((r) => { if (alive) { setMods(r.data.modules || {}); setTenantMods(r.data.tenant_modules || {}); } })
      .catch(() => { if (alive) { setMods({}); setTenantMods({}); } });
    return () => { alive = false; };
  }, [code, uid]);
  const levelOf = (key) => {
    if (mods === null) return "write";
    const v = mods[key];
    if (v === false || v === "none") return "none";
    if (v === true || v === undefined) return "write";
    return v;
  };
  // Phase 152: sub-feature levels ("module.feature") — effective (already capped by the module on the server);
  // an unknown / missing feature key simply inherits the module level.
  const featureLevel = (module, feature) => {
    if (mods === null) return "write";
    if (!feature) return levelOf(module);
    const v = mods[`${module}.${feature}`];
    if (v === undefined || v === null) return levelOf(module);
    if (v === false || v === "none") return "none";
    if (v === true) return levelOf(module);
    return RANK[v] <= RANK[levelOf(module)] ? v : levelOf(module);
  };
  return {
    loaded: mods !== null,
    allowed: (key) => levelOf(key) !== "none",
    levelOf,
    featureLevel,
    featureAllowed: (module, feature) => featureLevel(module, feature) !== "none",
    // "write" or "edit" both allow changes; view-only hides create / edit / delete controls
    canWrite: (module, feature) => RANK[featureLevel(module, feature)] >= RANK.edit,
    canCreate: (module, feature) => featureLevel(module, feature) === "write",
    // Phase 199: is a municipality-level module (Smart Parking) activated for this tenant? false while loading
    tenantModule: (key) => !!(tenantMods && tenantMods[key]),
    tenantModulesLoaded: tenantMods !== null,
  };
}

const RANK = { none: 0, view: 1, edit: 2, write: 3 };

/** First allowed value of an ordered list of tab descriptors [{value, feature}] for `module`, or null. */
export const firstAllowedTab = (access, module, tabs) => (tabs.find((t) => !t.feature || access.featureAllowed(module, t.feature)) || null)?.value || null;

// Redirects to the role home page when the module (or, Phase 152, the page's sub-feature) has been disabled.
export const ModuleGuard = ({ code, module, feature, home, children }) => {
  const { loaded, allowed, featureAllowed } = useRoleModules(code);
  if (loaded && (!allowed(module) || (feature && !featureAllowed(module, feature)))) return <Navigate to={home} replace />;
  return children;
};

// Fresh profile of the logged-in user (includes can_approve / parent_user_id even for
// sessions created before those fields existed).
export function useMe(code) {
  return useMeState(code).me;
}

// Phase 141: same profile fetch, but also tells the caller whether it is still loading / failed so
// the TerritoryGate can show a skeleton instead of flashing empty pages before the verdict arrives.
export function useMeState(code) {
  const uid = useSignedInUserId();
  const [state, setState] = useState({ me: null, loading: true, error: false });
  useEffect(() => {
    let alive = true;
    setState({ me: null, loading: true, error: false });
    api.get(`/t/${code}/auth/me`)
      .then((r) => { if (alive) setState({ me: r.data.user || null, loading: false, error: false }); })
      .catch(() => { if (alive) setState({ me: null, loading: false, error: true }); });
    return () => { alive = false; };
  }, [code, uid]);
  return state;
}
